import React, { useState, useRef, useEffect } from 'react';
import './style.css';

const AutocompleteSelect = ({
  value,
  onChange,
  options,
  placeholder = "Оберіть значення",
  loading = false,
  onSearch,
  error,
  label,
  disabled = false,
  className = '',
  fieldsValue = ['name']
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [hasSelected, setHasSelected] = useState(false);
  const [dropdownWidth, setDropdownWidth] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Находим выбранное значение для отображения
  const selectedOption = options.find(opt => opt._id === value);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    // Обновляем ширину dropdown при изменении размера
    const updateDropdownWidth = () => {
      if (wrapperRef.current) {
        setDropdownWidth(wrapperRef.current.offsetWidth);
      }
    };

    updateDropdownWidth();
    window.addEventListener('resize', updateDropdownWidth);
    
    return () => window.removeEventListener('resize', updateDropdownWidth);
  }, []);

  useEffect(() => {
    // Сбрасываем inputValue при изменении внешнего value
    if (value && selectedOption) {
      const valVisible = fieldsValue.map(f=>selectedOption[f]).join(" "); 
      setInputValue(valVisible);
      setHasSelected(true);
    } else {
      setInputValue('');
      setHasSelected(false);
    }
  }, [value, selectedOption]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);

        if(value && selectedOption){
          // Восстанавливаем значение при клике вне компонента
          const valVisible = fieldsValue.map(f=>selectedOption[f]).join(" "); 
      
          if (inputValue !== valVisible) {
            setInputValue(valVisible);
          }
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value, selectedOption, inputValue]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (newValue.length === 0) {
      // Если пользователь очистил поле - сбрасываем выбор
      onChange('');
      setHasSelected(false);
      setFilteredOptions(options);
      setIsOpen(false);
    } else if (newValue.length > 1) {
      if (onSearch) {
        onSearch(newValue);
      } else {
        const filtered = options.filter(option =>
          option.name.toLowerCase().includes(newValue.toLowerCase())
        );
        setFilteredOptions(filtered);
      }
      setIsOpen(true);
    } else {
      setFilteredOptions(options);
      setIsOpen(false);
    }
  };

  const handleSelect = (option) => {
    onChange(option._id);
    setInputValue(option.name);
    setHasSelected(true);
    setIsOpen(false);
  };

  const handleFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      setFilteredOptions(options);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      if (value && selectedOption) {
        setInputValue(selectedOption.name);
      }
    } else if (e.key === 'Enter' && isOpen && filteredOptions.length > 0) {
      // Выбираем первый элемент при нажатии Enter
      handleSelect(filteredOptions[0]);
    } else if (e.key === 'Backspace' && hasSelected && inputValue === selectedOption?.name) {
      // При нажатии Backspace на полностью выбранном значении - очищаем
      e.preventDefault();
      onChange('');
      setInputValue('');
      setHasSelected(false);
      setIsOpen(true);
    } else if (e.key === 'ArrowDown' && !isOpen) {
      // Открываем dropdown при стрелке вниз
      setIsOpen(true);
    }
  };

  const clearSelection = () => {
    onChange('');
    setInputValue('');
    setHasSelected(false);
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        inputRef.current?.focus();
      }
    }
  };

  return (
    <div className={`form-group autocomplete-container ${className}`} ref={wrapperRef}>
      {label && <label className="form-label">{label}</label>}
      
      <div className="autocomplete-wrapper">
        <div className="autocomplete-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            className={`form-control ${error ? 'is-invalid' : ''} ${disabled ? 'disabled' : ''}`}
            value={inputValue.length > 70 ?  inputValue.slice(0,70) + "..." : inputValue }
            onChange={handleInputChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={!onSearch && options.length === 0}
          />
          
          <div className="autocomplete-buttons">
            {hasSelected && (
              <button
                type="button"
                className="autocomplete-clear-btn"
                onClick={clearSelection}
                disabled={disabled}
                aria-label="Очистити вибір"
              >
                ×
              </button>
            )}
            
            <button
              type="button"
              className={`autocomplete-toggle-btn ${isOpen ? 'open' : ''}`}
              onClick={toggleDropdown}
              disabled={disabled}
              aria-label={isOpen ? "Згорнути список" : "Розгорнути список"}
            >
              ▼
            </button>
          </div>
        </div>
        
        {isOpen && (
          <div 
            className="autocomplete-dropdown"
            style={{ width: dropdownWidth }}
          >
            {loading ? (
              <div className="autocomplete-loading">
                <div className="spinner"></div>
                Завантаження...
              </div>
            ) : filteredOptions.length > 0 ? (
              <div className="autocomplete-list">
                {filteredOptions.map(option => (
                  <div
                    key={option._id}
                    className={`autocomplete-item ${value === option._id ? 'selected' : ''}`}
                    onClick={() => handleSelect(option)}
                  >
                    {option.name}
                    {option.description && (
                      <div className="autocomplete-item-description">
                        {option.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="autocomplete-no-results">
                {inputValue.length > 0 ? 'Нічого не знайдено' : 'Немає доступних опцій'}
              </div>
            )}
          </div>
        )}
        
        {error && <div className="invalid-feedback">{error}</div>}
      </div>
    </div>
  );
};

export default AutocompleteSelect;